import i18next, { t } from 'i18next'
import { List } from 'immutable'
import React from 'react'
import { none, some } from 'widgets-for-react'
import { pretty_date, visit_option } from '../../../../utils'
import { Pagination } from '../pagination.mod'
import { PublicationLibrary, SortKind } from './publicationLibrary.types'
import { NewToCurrentFilters, PublicationLibraryState, UpdateFilter, UpdateIndirectFilter } from './publicationLibrary.widget'
import { decodeHTMLEntities } from '../../../../helpers'
import { StringEmptyOrNull } from '../../../gravityform/utils'
import moment from 'moment'


export const PublicationLibraryLayout = (props: {
  data: PublicationLibraryState
  onPropChange: <K extends keyof PublicationLibraryState>(key: K, value: PublicationLibraryState[K]) => void
}) => {
  const { data, onPropChange } = props
  const { library_data, taxonomies, pagination_page, pagination_size, sort, current_filters, new_filters } = data

  const isLoading = library_data.count() != 0 && library_data.toIndexedSeq().toArray().reduce((xs, x) => xs && x.kind == 'loaded' && x.value.items.length == 0, true)

  const publications = library_data.toIndexedSeq()
    .toArray()
    .reduce((xs, x) => x.kind == 'loaded' ? xs.concat(x.value.items) : xs, Array<PublicationLibrary>())
    .filter(x =>
      visit_option(current_filters.search_word)(keyword => x.title.rendered.toLocaleLowerCase().includes(keyword) || x.acf.authors.reduce((a, b) => a + b.author_name.toLocaleLowerCase(), "").includes(keyword), () => true) && // Filter title and authors by search keyword
      visit_option(current_filters.date_from)(from_date => new Date(x.acf.publication_date).getTime() >= from_date.getTime(), () => true) && // Check if publication date is higher than filter date
      visit_option(current_filters.date_to)(to_date => new Date(x.acf.publication_date).getTime() <= to_date.getTime(), () => true) // Checki if publication date is lower than filter date
    )
    .sort((a, b) => {
      if (sort.v.v == 'date_asc') return new Date(a.acf.publication_date).getTime() - new Date(b.acf.publication_date).getTime()
      if (sort.v.v == 'title_asc') return a.title.rendered.localeCompare(b.title.rendered)
      if (sort.v.v == 'title_desc') return b.title.rendered.localeCompare(a.title.rendered)
      return new Date(b.acf.publication_date).getTime() - new Date(a.acf.publication_date).getTime() // date_desc
    })

  return <section className='block block--listing lst'>
    <div className="lst__inner container container--m gutter--m spacing--m">
      <aside className='lst__aside aside'>
        <div className="aside__inner">
          <h3 className='aside__title ff--primary typo--m'>Filter publications</h3>
          <div className='aside__group'>
            <label className='sr-text'>{i18next.t('publication.search_placeholder')}</label>
            <input className='aside__field aside__search form__input'
              placeholder={i18next.t('publication.search_placeholder')}
              value={visit_option<string, string>(new_filters.search_word)(v => v, () => "")}
              onChange={e => onPropChange('new_filters', UpdateIndirectFilter(new_filters, 'search_word', StringEmptyOrNull(e.currentTarget.value) ? none() : some(e.currentTarget.value.toLocaleLowerCase())))}
              onKeyDownCapture={e => {
                if (e.keyCode == 13) { // Enter
                  onPropChange('current_filters', UpdateFilter(current_filters, 'search_word', new_filters.search_word))
                }
              }}
            />
          </div>
          <div className='aside__group'>
            <label className='sr-text' htmlFor={i18next.t('publication.subjects')}>{i18next.t('publication.subjects')}</label>
            <select id={i18next.t('publication.subjects')}
              className="aside__field dropdown"
              aria-label={i18next.t('publication.subjects')}
              value={visit_option<number, number>(current_filters.selected_term)(v => v, () => -1)}
              onChange={e => onPropChange('current_filters', UpdateFilter(current_filters, 'selected_term', Number(e.currentTarget.value) < 0 ? none() : some(Number(e.currentTarget.value))))}
            >
              <option value={-1}>{i18next.t('publication.subjects')}</option>
              {taxonomies.kind == 'loaded' && taxonomies.value.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className='aside__group aside__group--s'>
            <label className='sr-text'>{i18next.t('publication.date_from')}</label>
            <input className='aside__date aside__field'
              placeholder={i18next.t('publication.date_from')}
              type="date"
              value={visit_option<Date, string>(new_filters.date_from)(v => moment(v).format('YYYY-MM-DD'), () => null)}
              onChange={e => onPropChange('new_filters', UpdateIndirectFilter(new_filters, 'date_from', StringEmptyOrNull(e.currentTarget.value) ? none() : some(new Date(e.currentTarget.value))))}
              onKeyDownCapture={e => {
                if (e.keyCode == 13) { // Enter
                  onPropChange('current_filters', UpdateFilter(current_filters, 'date_from', new_filters.date_from))
                }
              }}
            />
          </div>
          <div className='aside__group aside__group--s'>
            <label className='sr-text'>{i18next.t('publication.date_to')}</label>
            <input className='aside__field aside__date'
              placeholder={i18next.t('publication.date_to')}
              type="date"
              value={visit_option<Date, string>(new_filters.date_to)(v => moment(v).format('YYYY-MM-DD'), () => null)}
              onChange={e => onPropChange('new_filters', UpdateIndirectFilter(new_filters, 'date_to', StringEmptyOrNull(e.currentTarget.value) ? none() : some(new Date(e.currentTarget.value))))}
              onKeyDownCapture={e => {
                if (e.keyCode == 13) { // Enter
                  onPropChange('current_filters', UpdateFilter(current_filters, 'date_to', new_filters.date_to))
                }
              }}
            />
          </div>
          <button className='aside__button button button--primary'
            onClick={() => onPropChange('current_filters', NewToCurrentFilters(new_filters, current_filters))}
          >
            {i18next.t('publication.search_button')}
          </button>
        </div>
      </aside>
      <div className="lst__content">
        <div className='lst__filters'>
          <select className="lst__dropdown dropdown typo--s"
            value={visit_option<string, string>(sort)(v => v, () => "")}
            onChange={e => onPropChange('sort', e.currentTarget.value == "" ? none<SortKind>() : some<SortKind>(e.currentTarget.value as SortKind))}
          >
            <option value={""}>{i18next.t('publication.sort')}</option>
            <option value={"date_desc"}>{i18next.t('publication.date_newest')}</option>
            <option value={"date_asc"}>{i18next.t('publication.date_oldest')}</option>
            <option value={"title_asc"}>{i18next.t('publication.title_a_z')}</option>
            <option value={"title_desc"}>{i18next.t('publication.title_z_a')}</option>
          </select>
        </div>
        <ul className="lst__list">
          {
            List(publications)
              .skip(pagination_size * (pagination_page - 1))
              .take(pagination_size)
              .map(entry => <li className="lst__item" key={entry.id}>
                <a className="lst__link" target='_blank' href={entry.acf.insert_link === true ? entry.acf.external_source : entry.acf.file} download={entry.acf.insert_link !== true}>
                  <time className="lst__date typo--s" dateTime={pretty_date(entry.acf.publication_date)}>{pretty_date(entry.acf.publication_date)}</time>
                  <h3 className="lst__title ff--primary typo--ml">{decodeHTMLEntities(entry.title.rendered)}</h3>
                  <p className="lst__intro">{entry.acf.authors.reduce((xs, x, i) => xs + x.author_name + (entry.acf.authors.length - 1 == i ? '' : ", "), '')}</p>
                </a>
              </li>)
          }
          {isLoading && <h3 className="lst__title ff--primary typo--ml">{i18next.t('publication.no_results')}</h3>}
        </ul>
        {(publications.length / pagination_size) > 1 && (
          <Pagination
            pager={{
              current_page: pagination_page,
              items_per_page: pagination_size,
              total_items: publications.length,
              total_pages: publications.length / pagination_size,
            }}
            setPage={(page) => onPropChange("pagination_page", page)}
          />
        )}
      </div>
    </div>
  </section>
}